import { TrendingUp, TrendingDown, Edit3, FileText } from "lucide-react";

export type TransactionType = "receipt" | "dispense" | "adjustment";

export interface TransactionDetails {
  invoice_number?: string;
  rx_number?: string;
  reason?: string;
  created_by_initials?: string;
  wholesaler?: string;
  lot_number?: string;
  expiration_date?: string;
  is_duplicate_rx?: boolean;
  [key: string]: string | number | boolean | undefined;
}

export interface Transaction {
  date: string;
  transaction_type: TransactionType;
  transaction_id: string;
  quantity_change: number;
  running_stock: number;
  details: string | TransactionDetails;
  reference_number?: string;
  reason?: string;
  created_by_name?: string;
}

export interface ApiTransaction {
  date: string;
  type: TransactionType;
  transactionId: string;
  quantity: number;
  runningStock: number;
  details?: TransactionDetails;
}

/**
 * Gets the appropriate icon for a transaction type
 */
export function getTransactionIcon(type: TransactionType) {
  switch (type) {
    case "receipt":
      return <TrendingUp className="h-4 w-4" />;
    case "dispense":
      return <TrendingDown className="h-4 w-4" />;
    case "adjustment":
      return <Edit3 className="h-4 w-4" />;
    default:
      return <FileText className="h-4 w-4" />;
  }
}

/**
 * Gets the appropriate color classes for a transaction type
 */
export function getTransactionColor(type: TransactionType): string {
  switch (type) {
    case "receipt":
      return "text-green-600 bg-green-50 border-green-200";
    case "dispense":
      return "text-red-600 bg-red-50 border-red-200";
    case "adjustment":
      return "text-yellow-600 bg-yellow-50 border-yellow-200";
    default:
      return "";
  }
}

/**
 * Gets the appropriate border color classes for timeline nodes
 */
export function getTransactionNodeColor(type: TransactionType): string {
  switch (type) {
    case "receipt":
      return "border-green-500 text-green-600";
    case "dispense":
      return "border-red-500 text-red-600";
    case "adjustment":
      return "border-yellow-500 text-yellow-600";
    default:
      return "border-gray-500 text-gray-600";
  }
}

/**
 * Determines if a transaction type is clickable (navigable)
 */
export function isTransactionClickable(type: TransactionType): boolean {
  return type === "receipt" || type === "dispense";
}

/**
 * Formats transaction details into a human-readable string
 */
export function formatTransactionDetails(
  transaction: Transaction | ApiTransaction
): string {
  let details: TransactionDetails;

  // Handle both string and object details
  if (typeof transaction.details === "string") {
    try {
      details = JSON.parse(transaction.details);
    } catch {
      return transaction.details;
    }
  } else if (transaction.details) {
    details = transaction.details;
  } else {
    return "Transaction";
  }

  const type =
    "transaction_type" in transaction
      ? transaction.transaction_type
      : transaction.type;

  switch (type) {
    case "receipt": {
      const receiptParts = [];
      if (details.wholesaler)
        receiptParts.push(`Wholesaler: ${details.wholesaler}`);
      if (details.lot_number) receiptParts.push(`Lot: ${details.lot_number}`);
      if (details.expiration_date)
        receiptParts.push(`Exp: ${details.expiration_date}`);
      return receiptParts.length > 0
        ? receiptParts.join(" • ")
        : "Receipt transaction";
    }

    case "dispense": {
      const dispenseParts = [];
      if (details.is_duplicate_rx) dispenseParts.push("Duplicate Rx");
      return dispenseParts.length > 0
        ? dispenseParts.join(" • ")
        : "Dispense transaction";
    }

    case "adjustment":
      return details.reason || "Stock adjustment";

    default:
      return "Transaction";
  }
}

/**
 * Maps API transaction to internal transaction format
 */
export function mapApiTransaction(txn: ApiTransaction): Transaction {
  return {
    date: txn.date,
    transaction_type: txn.type,
    transaction_id: txn.transactionId,
    quantity_change: txn.quantity,
    running_stock: txn.runningStock,
    details: JSON.stringify(txn.details),
    reference_number: txn.details?.invoice_number || txn.details?.rx_number,
    reason: txn.details?.reason,
    created_by_name: txn.details?.created_by_initials,
  };
}
